import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Moon, Clock, Check, X, ArrowClockwise } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

export default function ProRequests() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counterFor, setCounterFor] = useState(null);
  const [counterTime, setCounterTime] = useState("");
  const [counterMsg, setCounterMsg] = useState("");

  const load = () => api.get("/after_hours/mine").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  useEffect(() => { if (user) load(); else setLoading(false); }, [user]);

  const accept = async (rid) => {
    try { await api.post(`/after_hours/${rid}/accept`); toast.success("Accepted — booking created."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Accept failed"); }
  };
  const decline = async (rid) => {
    try { await api.post(`/after_hours/${rid}/decline`); toast.success("Declined."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Decline failed"); }
  };
  const submitCounter = async () => {
    if (!counterTime) { toast.error("Pick a counter time"); return; }
    try {
      const iso = new Date(counterTime).toISOString();
      await api.post(`/after_hours/${counterFor}/counter`, { counter_time: iso, message: counterMsg });
      toast.success("Counter sent.");
      setCounterFor(null); setCounterTime(""); setCounterMsg("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Counter failed"); }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-5 py-10 text-cream/50">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-5 pt-8 pb-24" data-testid="pro-requests-page">
      <p className="overline mb-2 flex items-center gap-2 justify-start"><Moon size={12} weight="fill" color="#C5A059" /> After-hours</p>
      <h1 className="font-display text-4xl md:text-5xl tracking-tighter text-cream">Requests</h1>
      <p className="text-cream/60 mt-2 text-sm">Custom-time requests from Members. Accept, Counter, or Decline.</p>

      {!items.length && (
        <div className="mt-10 border border-cream/10 p-6 text-center" data-testid="pro-requests-empty">
          <p className="text-cream/70">No after-hours requests yet.</p>
        </div>
      )}

      <div className="mt-8 space-y-4">
        {items.map((r) => (
          <div key={r.id} className="border border-cream/10 p-5" data-testid={`pro-request-${r.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-display text-xl text-cream">{r.member_name}</p>
                <p className="text-sm text-cream/80 mt-1">{r.service_name} · ${r.service_price} · {r.duration_minutes} min</p>
                <p className="text-xs text-cream/60 mt-1 flex items-center gap-2"><Clock size={12} /> Proposed {new Date(r.proposed_time).toLocaleString()}</p>
                {r.counter_time && <p className="text-xs text-gold mt-1">Countered {new Date(r.counter_time).toLocaleString()}</p>}
                {r.notes && <p className="text-xs text-cream/70 mt-2 italic">"{r.notes}"</p>}
              </div>
              <span className="chip text-[10px]">{r.status.replace(/_/g, " ")}</span>
            </div>

            {r.status === "pending" && (
              <div className="mt-4 flex gap-2 flex-wrap">
                <button data-testid={`req-accept-${r.id}`} onClick={() => accept(r.id)} className="btn-gold px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold inline-flex items-center gap-1"><Check size={12} weight="bold" /> Accept</button>
                <button data-testid={`req-counter-${r.id}`} onClick={() => setCounterFor(r.id === counterFor ? null : r.id)} className="btn-ghost px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1"><ArrowClockwise size={12} /> Counter</button>
                <button data-testid={`req-decline-${r.id}`} onClick={() => decline(r.id)} className="btn-ghost px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1 text-red-300 border-red-300/30"><X size={12} /> Decline</button>
              </div>
            )}

            {counterFor === r.id && (
              <div className="mt-4 border border-cream/10 p-4 space-y-3" data-testid={`counter-form-${r.id}`}>
                <input type="datetime-local" data-testid={`counter-time-${r.id}`} value={counterTime} onChange={(e) => setCounterTime(e.target.value)} className="w-full bg-transparent border border-cream/15 p-2 text-cream text-sm" />
                <input type="text" data-testid={`counter-msg-${r.id}`} placeholder="Message (optional)" value={counterMsg} onChange={(e) => setCounterMsg(e.target.value)} className="w-full bg-transparent border border-cream/15 p-2 text-cream text-sm" />
                <button data-testid={`counter-send-${r.id}`} onClick={submitCounter} className="btn-gold w-full px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold">Send counter</button>
              </div>
            )}

            {r.status === "accepted" && r.booking_id && (
              <Link to="/bookings" data-testid={`req-view-booking-${r.id}`} className="mt-4 inline-block btn-ghost px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em]">View in bookings</Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
